import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from '@/lib/api-client';

/**
 * Translates a thrown API error into a message for the user.
 *
 * The server's stable `code` is what gets translated; its English `message` is
 * never shown, so the UI stays fully Arabic without the backend owning any
 * copy.
 */
export function useApiError() {
  const { t } = useTranslation('errors');

  return (error: unknown): { message: string; reference?: string } => {
    // Rule E8: the command may already have succeeded server-side — this is
    // never worded like a confirmed failure, and never invites a blind retry
    // of a posted currency movement.
    if (error instanceof ApiTimeoutError) return { message: t('timeout') };

    if (error instanceof ApiRequestError) {
      return {
        message: t(error.code, { defaultValue: t('INTERNAL_ERROR') }),
        // Rule E15: only a reference the server actually issued. The synthetic
        // fallback identifies nothing, and showing it sends a user to support
        // with an id no one can look up.
        ...(error.requestId === UNKNOWN_REQUEST_ID
          ? {}
          : { reference: t('referenceId', { requestId: error.requestId }) }),
      };
    }

    return { message: t('network') };
  };
}

/**
 * Maps `error.fieldErrors` (validation) onto the matching react-hook-form
 * field, so a drawer form shows the problem next to the cause instead of only
 * a generic toast (ui-contract.md section 6). Ported from the Customers /
 * Unified Access web kit's `useApplyFieldErrors`. Returns whether it applied
 * anything, so the caller can fall back to a toast otherwise.
 */
export function useApplyFieldErrors() {
  const { t } = useTranslation('errors');

  return <T extends FieldValues>(form: UseFormReturn<T>, error: unknown): boolean => {
    if (!(error instanceof ApiRequestError)) return false;
    const fieldErrors = error.fieldErrors;
    if (fieldErrors.length === 0) return false;

    const message = t(error.code, { defaultValue: t('INTERNAL_ERROR') });
    let applied = false;
    for (const entry of fieldErrors) {
      const path = entry.path;
      if (typeof path !== 'string' || path.length === 0) continue;
      form.setError(path as Path<T>, { type: 'server', message: entry.message || message });
      applied = true;
    }
    return applied;
  };
}
